/**
 * AI Difficulty Settings - configures AI behavior based on difficulty level
 */

export type DifficultyLevel = 'easy' | 'normal' | 'hard' | 'expert';

export interface DifficultyModifiers {
  /** Multiplier for AI gold income */
  goldIncomeMultiplier: number;
  /** Multiplier for AI unit spawn rate */
  spawnRateMultiplier: number;
  /** Multiplier for AI unit damage */
  damageMultiplier: number;
  /** Multiplier for AI unit health */
  healthMultiplier: number;
  /** How often AI updates strategy (lower = more reactive) */
  strategicUpdateInterval: number;
  /** How often AI makes tactical decisions (lower = faster) */
  tacticalUpdateInterval: number;
  /** Aggression boost (added to personality aggression) */
  aggressionBoost: number;
  /** Whether AI gets vision bonus */
  hasVisionBonus: boolean;
  /** Building cost discount for AI (1.0 = normal, 0.8 = 20% cheaper) */
  buildingCostMultiplier: number;
}

export const DIFFICULTY_SETTINGS: Record<DifficultyLevel, DifficultyModifiers> = {
  easy: {
    goldIncomeMultiplier: 0.7,
    spawnRateMultiplier: 0.7,
    damageMultiplier: 0.8,
    healthMultiplier: 0.9,
    strategicUpdateInterval: 45,
    tacticalUpdateInterval: 8,
    aggressionBoost: -0.2,
    hasVisionBonus: false,
    buildingCostMultiplier: 1.2,
  },
  normal: {
    goldIncomeMultiplier: 1.0,
    spawnRateMultiplier: 1.0,
    damageMultiplier: 1.0,
    healthMultiplier: 1.0,
    strategicUpdateInterval: 30,
    tacticalUpdateInterval: 5,
    aggressionBoost: 0,
    hasVisionBonus: false,
    buildingCostMultiplier: 1.0,
  },
  hard: {
    goldIncomeMultiplier: 1.3,
    spawnRateMultiplier: 1.2,
    damageMultiplier: 1.15,
    healthMultiplier: 1.1,
    strategicUpdateInterval: 20,
    tacticalUpdateInterval: 3,
    aggressionBoost: 0.1,
    hasVisionBonus: true,
    buildingCostMultiplier: 0.9,
  },
  expert: {
    goldIncomeMultiplier: 1.6,
    spawnRateMultiplier: 1.4,
    damageMultiplier: 1.3,
    healthMultiplier: 1.2,
    strategicUpdateInterval: 15,
    tacticalUpdateInterval: 2,
    aggressionBoost: 0.2,
    hasVisionBonus: true,
    buildingCostMultiplier: 0.75,
  },
};

/**
 * Get difficulty modifiers for a given level
 */
export function getDifficultyModifiers(level: DifficultyLevel): DifficultyModifiers {
  return DIFFICULTY_SETTINGS[level];
}

/**
 * Get display name for difficulty level
 */
export function getDifficultyName(level: DifficultyLevel): string {
  const names: Record<DifficultyLevel, string> = {
    easy: '简单',
    normal: '普通',
    hard: '困难',
    expert: '专家',
  };
  return names[level];
}

/**
 * Get description for difficulty level
 */
export function getDifficultyDescription(level: DifficultyLevel): string {
  const descriptions: Record<DifficultyLevel, string> = {
    easy: 'AI势力较弱，攻击性低。适合新手学习。',
    normal: '平衡的游戏体验，公平的AI对手。',
    hard: 'AI势力更强，策略更高明。真正的挑战。',
    expert: 'AI获得最大加成和攻击性。仅限资深玩家。',
  };
  return descriptions[level];
}
